import apiV2Service from '../api/api-v2.service';
import canvasToFile from '../../utils/fileUtils/canvasToFile';
import getFileFromLink from '../../utils/fileUtils/getFileFromLink';
import {addBase64Prefix, toClientGenAiService, toRemoteMaskImage} from './aiImages.dataConverter';
import {
  AIImageStyle,
  AssetMetadata,
  ExtractTextLayersResponse,
  GenAiService,
  ImageReference,
  ProductShotRequest,
  PromptToImageRequest,
  PromptToImageResponse,
  RemoteGenAiService,
  RemoteVendorService,
} from './aiImages.types';
import _keyBy from 'lodash/keyBy';
import _map from 'lodash/map';
import {GenAIActionType} from './aiImages.enums';
import {CancelToken} from 'axios';
import {PostDataObj} from '../api/apiTypes';

const BASE_URL = 'AIImages';

function getImageStyles() {
  return apiV2Service
    .executeGetRequest<{styles: AIImageStyle[]}>(`${BASE_URL}/ImageStyles`)
    .then((res) => res.data.styles);
}

// Image Editor V2

async function getServicePerAction(): Promise<Record<GenAIActionType, GenAiService>> {
  const {data} = await apiV2Service.executeGetRequest<RemoteGenAiService[]>(
    `${BASE_URL}/ServicePerAction`,
  );

  const convertedData = _map(data, toClientGenAiService);

  return _keyBy(convertedData, 'action') as Record<GenAIActionType, GenAiService>;
}

async function inpaint({
  vendorService,
  outputCount,
  prompt,
  maskCanvas,
  sourceCanvas,
  cancelToken,
}: {
  vendorService: number;
  outputCount: number;
  prompt: string;
  sourceCanvas: HTMLCanvasElement;
  maskCanvas: HTMLCanvasElement;
  cancelToken: CancelToken;
}) {
  const mask = toRemoteMaskImage(maskCanvas);
  const sourceImage = await canvasToFile(sourceCanvas, 'image/png', 'source.png');
  const maskImage = await canvasToFile(mask, 'image/png', 'mask.png');

  const {data} = await apiV2Service.executePostRequest<{images: string[]}>(
    `${BASE_URL}/Inpaint`,
    {},
    {
      sourceImage,
      maskImage,
      prompt,
      outputCount,
      vendorService,
    },
    {sendAsFormData: true, cancelToken},
  );

  return data.images.map(addBase64Prefix);
}

async function outpaint({
  sourceCanvas,
  prompt,
  cancelToken,
  left,
  right,
  top,
  bottom,
  outputCount,
  vendorService,
}: {
  sourceCanvas: HTMLCanvasElement;
  prompt: string;
  left: number;
  right: number;
  top: number;
  bottom: number;
  cancelToken: CancelToken;
  outputCount: number;
  vendorService: number;
}) {
  const sourceImage = await canvasToFile(sourceCanvas, 'image/png', 'source.png');

  const {data} = await apiV2Service.executePostRequest<{images: string[]}>(
    `${BASE_URL}/Outpaint`,
    {},
    {
      sourceImage,
      prompt,
      left,
      right,
      up: top,
      down: bottom,
      outputCount,
      vendorService,
    },
    {sendAsFormData: true, cancelToken},
  );

  return data.images.map(addBase64Prefix);
}

async function upscale({
  sourceCanvas,
  scaleFactor,
  cancelToken,
  vendorService,
}: {
  sourceCanvas: HTMLCanvasElement;
  scaleFactor: number;
  cancelToken: CancelToken;
  vendorService: number;
}) {
  const sourceImage = await canvasToFile(sourceCanvas, 'image/png', 'source.png');

  const {data} = await apiV2Service.executePostRequest<{image: string}>(
    `${BASE_URL}/Upscale`,
    {},
    {
      sourceImage,
      scaleFactor,
      vendorService,
    },
    {sendAsFormData: true, cancelToken},
  );

  return addBase64Prefix(data.image);
}

async function removeBackground({
  sourceCanvas,
  cancelToken,
  vendorService,
}: {
  sourceCanvas: HTMLCanvasElement;
  cancelToken: CancelToken;
  vendorService: number;
}) {
  const sourceImage = await canvasToFile(sourceCanvas, 'image/png', 'source.png');

  const {data} = await apiV2Service.executePostRequest<{image: string}>(
    `${BASE_URL}/RemoveBackground`,
    {},
    {
      sourceImage,
      vendorService,
    },
    {sendAsFormData: true, cancelToken},
  );

  return addBase64Prefix(data.image);
}

async function replaceBackground({
  sourceCanvas,
  prompt,
  cancelToken,
  outputCount,
  vendorService,
}: {
  sourceCanvas: HTMLCanvasElement;
  prompt: string;
  cancelToken: CancelToken;
  outputCount: number;
  vendorService: number;
}) {
  const sourceImage = await canvasToFile(sourceCanvas, 'image/png', 'source.png');

  const {data} = await apiV2Service.executePostRequest<{images: string[]}>(
    `${BASE_URL}/ReplaceBackground`,
    {},
    {
      sourceImage,
      backgroundPrompt: prompt,
      outputCount,
      vendorService,
    },
    {sendAsFormData: true, cancelToken},
  );

  return data.images.map(addBase64Prefix);
}

async function promptToImage(payload: PromptToImageRequest): Promise<string[]> {
  const {data} = await apiV2Service.executePostRequest<PromptToImageResponse>(
    `${BASE_URL}/PromptToImage`,
    {},
    payload,
  );

  return data.images.map(addBase64Prefix);
}

async function analyzeImage({
  sourceCanvas,
  vendorService,
  cancelToken,
}: {
  sourceCanvas: HTMLCanvasElement;
  vendorService: number;
  cancelToken: CancelToken;
}): Promise<AssetMetadata> {
  const sourceImage = await canvasToFile(sourceCanvas, 'image/png', 'source.png');

  const {data} = await apiV2Service.executePostRequest<{assetMetadata: AssetMetadata}>(
    `${BASE_URL}/AnalyzeImage`,
    {},
    {
      sourceImage,
      vendorService,
    },
    {sendAsFormData: true, cancelToken},
  );

  return data.assetMetadata;
}

async function analyzeImageFromUrl({
  imageUrl,
  vendorService,
  cancelToken,
}: {
  imageUrl: string;
  vendorService: number;
  cancelToken: CancelToken;
}): Promise<AssetMetadata> {
  const sourceImage = await getFileFromLink(imageUrl, 'image.png');

  const {data} = await apiV2Service.executePostRequest<{assetMetadata: AssetMetadata}>(
    `${BASE_URL}/AnalyzeImage`,
    {},
    {
      sourceImage,
      vendorService,
    },
    {sendAsFormData: true, cancelToken},
  );

  return data.assetMetadata;
}

async function extractTextLayersFromImageUrl({
  imageUrl,
  mergeLevel,
  vendorService,
  cancelToken,
}: {
  imageUrl: string;
  mergeLevel?: number | null;
  vendorService: number;
  cancelToken: CancelToken;
}): Promise<ExtractTextLayersResponse> {
  const sourceImage = await getFileFromLink(imageUrl, 'image.png');

  const {data} = await apiV2Service.executePostRequest<ExtractTextLayersResponse>(
    `${BASE_URL}/ExtractTextLayersFromImage`,
    {},
    {
      sourceImage,
      ...(mergeLevel !== undefined && {mergeLevel}),
      vendorService,
    },
    {sendAsFormData: true, cancelToken},
  );

  return data;
}

function ensureHttps(url: string | null): string {
  if (!url) return '';

  return url.startsWith('//') ? `https:${url}` : url;
}

async function promptToImageWithReferenceImages(
  prompt: string,
  images: ImageReference[],
  width: number,
  height: number,
  outputCount: number,
  vendorService: number,
  options?: {
    negativePrompt?: string | null;
    transparentBackground?: boolean;
    quality?: string;
    focusRectangle?: {
      x: number;
      y: number;
      w: number;
      h: number;
    };
  },
): Promise<string[]> {
  const payload: PostDataObj = {
    prompt,
    width,
    height,
    outputCount,
    vendorService,
    images: images.map((image) => ({
      name: image.name || '',
      url: ensureHttps(image.url),
    })),
    ...options,
  };

  const {data} = await apiV2Service.executePostRequest<{images: string[]}>(
    `${BASE_URL}/PromptToImageWithReferenceImages`,
    {},
    payload,
  );

  return data.images.map(addBase64Prefix);
}

async function editImageByPrompt({
  sourceCanvas,
  prompt,
  cancelToken,
  vendorService,
  outputCount,
}: {
  sourceCanvas: HTMLCanvasElement;
  prompt: string;
  cancelToken: CancelToken;
  vendorService: number;
  outputCount: number;
}) {
  const sourceImage = await canvasToFile(sourceCanvas, 'image/png', 'source.png');

  const {data} = await apiV2Service.executePostRequest<{images: string[]}>(
    `${BASE_URL}/EditImageByPrompt`,
    {},
    {sourceImage, prompt, outputCount, vendorService},
    {sendAsFormData: true, cancelToken},
  );

  return data.images.map(addBase64Prefix);
}

/**
 * Get vendor service details by ID
 */
const vendorServiceCache = new Map<number, Promise<RemoteVendorService>>();

async function getVendorServiceById(vendorServiceId: number): Promise<RemoteVendorService> {
  const cached = vendorServiceCache.get(vendorServiceId);

  if (cached) {
    return cached;
  }

  const promise = apiV2Service
    .executeGetRequest<RemoteVendorService>(`${BASE_URL}/GetVendorService/${vendorServiceId}`)
    .then(({data}) => data);

  vendorServiceCache.set(vendorServiceId, promise);

  return promise;
}

async function productShot(payload: ProductShotRequest): Promise<string[]> {
  const requestPayload: PostDataObj = {
    productImage: {
      name: payload.productImage.name || '',
      url: ensureHttps(payload.productImage.url),
    },
    width: payload.width,
    height: payload.height,
    outputCount: payload.outputCount,
    vendorService: payload.vendorService,
    ...(payload.scenePrompt && {scenePrompt: payload.scenePrompt}),
    ...(payload.sceneImage && {
      sceneImage: {
        name: payload.sceneImage.name || '',
        url: ensureHttps(payload.sceneImage.url),
      },
    }),
    ...(payload.focalRect && {focalRect: payload.focalRect}),
    ...(payload.productShotType !== undefined && {productType: payload.productShotType}),
  };

  const {data} = await apiV2Service.executePostRequest<{images: string[]}>(
    `${BASE_URL}/ProductShot`,
    {},
    requestPayload,
  );

  return data.images.map(addBase64Prefix);
}

const aiImagesApi = {
  getImageStyles,

  // Image Editor V2
  getServicePerAction,
  getVendorServiceById,
  inpaint,
  outpaint,
  upscale,
  removeBackground,
  replaceBackground,
  promptToImage,
  promptToImageWithReferenceImages,
  productShot,
  analyzeImage,
  analyzeImageFromUrl,
  extractTextLayersFromImageUrl,
  editImageByPrompt,
};

export default aiImagesApi;
